/**
 * OverdueWarningCallSheet · iter303ak-fix44 — Overdue · T30 · Action 10
 * ─────────────────────────────────────────────────────────────────────
 * TWO-STAGE warning call sheet (parity with T20 OverdueContactCallSheet).
 * Escalated tone — 30+ days overdue, last-chance before Tribunal filing.
 *
 *   Stage 1 — client/property panel · unpaid summary · Tribunal-tone script · outcome pick
 *   Stage 2 — outcome-specific follow-up form (agreed / plan / soft-promise /
 *             VM-or-none / refused) with scripted questions, urgency-tight
 *             recall dates, and (for refused) a Tribunal filing deadline.
 *
 * Persists nested at `overdue_warning_call.{outcome, notes, called_at, follow_up:{...}}`
 * so the T30 A20 Formal Payment Warning letter can render outcome-specific copy.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Phone, Mail, MapPin, Calendar, DollarSign, AlertTriangle, CheckCircle2, ArrowLeft, Clock, Voicemail, PhoneOff, AlertCircle, HandCoins, Scale } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (baseISO, days) => {
  const d = baseISO ? new Date(baseISO) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const nowStamp = () => new Date().toISOString();

// Recall defaults tighter than T20 (escalation urgency)
const OUTCOMES = [
  { key: 'agreed_to_pay',       label: 'Agreed to pay in full',       color: 'emerald', icon: HandCoins,     next_hint: 'Capture method + date. Recheck tomorrow.', recall_default_days: 2 },
  { key: 'wants_payment_plan',  label: 'Wants a payment plan',        color: 'amber',   icon: Calendar,       next_hint: 'Skip to T40 Payment Plan Negotiation.', recall_default_days: 2 },
  { key: 'promised_soon',       label: 'Promised — no firm date',     color: 'sky',     icon: Clock,          next_hint: 'Warning letter still goes out as record.', recall_default_days: 3 },
  { key: 'voicemail_or_none',   label: 'Voicemail / no answer',       color: 'slate',   icon: Voicemail,      next_hint: 'Warning letter goes out. Retry in 3 days.', recall_default_days: 3 },
  { key: 'refused_tribunal',    label: 'Refused → prepare Tribunal',  color: 'rose',    icon: Scale,          next_hint: 'Warning letter, then T50 Tribunal Prep.', recall_default_days: 3 },
];

const PAYMENT_METHODS = ['Bank transfer', 'Cheque', 'Cash', 'Card', 'Other'];
const FREQUENCIES = ['weekly', 'fortnightly', 'monthly'];
const REFUSE_REASONS = [
  { key: 'cant_afford',   label: "Can't afford" },
  { key: 'bad_work',      label: 'Unhappy with the work' },
  { key: 'hardship',      label: 'Financial hardship / illness' },
  { key: 'no_reason',     label: 'No reason given' },
  { key: 'other',         label: 'Other (note below)' },
];

const VM_SCRIPT_DEFAULT = (name, addr, total) =>
  `Hi ${name || 'there'}, this is Trevor from Roofs To Remedy. This is a formal follow-up call — the account for the roofing work at ${addr || 'your property'} is now more than 30 days overdue, with $${total} still owing. ` +
  `A formal warning letter is being sent to you today. Unless we hear from you within the next 7 days to arrange payment or a payment plan, we will file this debt with the New Zealand Disputes Tribunal. Please call us back urgently. Thank you.`;

export default function OverdueWarningCallSheet({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parent = useMemo(() => (capturedData?.appraisal_record || capturedData?.appraisal || {}), [capturedData]);
  const client = capturedData?.client_record || capturedData?.client || {};
  const prop = capturedData?.property_record || capturedData?.property || {};

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const payments = parent.invoiced_payments || {};
  const wexec = parent.work_execution || {};
  const depositPick = parent.per_child_deposit_pick || {};

  const unpaidChildren = acceptedChildren.filter(c => {
    const s = payments[c.child_id]?.status;
    return s !== 'paid' && s !== 'written_off';
  });

  const clientName = [client.name_title, client.name_first, client.name_last].filter(Boolean).join(' ') || 'Client';
  const propAddress = [
    `${prop.property_address_num || ''} ${prop.property_address_street || ''}`.trim(),
    prop.property_address_suburb, prop.property_address_city,
  ].filter(Boolean).join(', ') || '—';
  const totalDue = unpaidChildren.reduce((a, c) => a + Number(payments[c.child_id]?.remaining || 0), 0);
  const callbackPhone = '027 XXX XXXX';

  const daysOverdue = useMemo(() => {
    const dates = unpaidChildren.map(c => c.invoice_due_date).filter(Boolean);
    if (!dates.length) return null;
    const earliest = dates.sort()[0];
    try {
      const diff = Math.floor((Date.now() - new Date(earliest).getTime()) / 86400000);
      return diff > 0 ? diff : null;
    } catch { return null; }
  }, [unpaidChildren]);

  // ── Root state ──────────────────────────────────────────────
  const [outcome, setOutcome]   = useState(value?.outcome || '');
  const [notes, setNotes]       = useState(value?.notes || '');
  const [calledAt, setCalledAt] = useState(value?.called_at || todayISO());
  const [followUp, setFollowUp] = useState(value?.follow_up || {});

  const meta = OUTCOMES.find(o => o.key === outcome);

  // Seed defaults per outcome
  useEffect(() => {
    if (!outcome) return;
    setFollowUp(prev => {
      if (prev && prev._seeded_for === outcome) return prev;
      const base = { _seeded_for: outcome, stamped_at: nowStamp() };
      if (outcome === 'agreed_to_pay') {
        return {
          ...base,
          payment_method: PAYMENT_METHODS[0],
          amount: totalDue ? Number(totalDue.toFixed(2)) : 0,
          promised_date: addDaysISO(null, 3),
          recall_date: addDaysISO(null, meta?.recall_default_days || 2),
          bank_details_needed: false,
          script_qs: { warned_of_tribunal: false, asked_amount: false, asked_when: false, asked_method: false },
        };
      }
      if (outcome === 'wants_payment_plan') {
        return {
          ...base,
          frequency: 'weekly',
          instalment_amount: 0,
          proposed_start: addDaysISO(null, 5),
          upfront_deposit: 0,
          recall_date: addDaysISO(null, meta?.recall_default_days || 2),
          script_qs: { warned_of_tribunal: false, asked_capacity: false, asked_start: false, asked_upfront: false },
        };
      }
      if (outcome === 'promised_soon') {
        return {
          ...base,
          estimated_amount: totalDue ? Number(totalDue.toFixed(2)) : 0,
          estimated_by: addDaysISO(null, 7),
          recall_date: addDaysISO(null, meta?.recall_default_days || 3),
          script_qs: { warned_of_tribunal: false, asked_firm_date: false, offered_plan: false },
        };
      }
      if (outcome === 'voicemail_or_none') {
        return {
          ...base,
          sub_outcome: 'voicemail',
          vm_script: VM_SCRIPT_DEFAULT(clientName, propAddress, fmt(totalDue)),
          time_of_call: new Date().toTimeString().slice(0, 5),
          callback_number_left: callbackPhone,
          recall_date: addDaysISO(null, meta?.recall_default_days || 3),
        };
      }
      if (outcome === 'refused_tribunal') {
        return {
          ...base,
          reason: '',
          reason_note: '',
          final_request_read: false,
          tribunal_deadline: addDaysISO(null, meta?.recall_default_days || 3),
          recall_date: addDaysISO(null, meta?.recall_default_days || 3),
        };
      }
      return base;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);

  useEffect(() => {
    if (!onChange) return;
    onChange({ outcome, notes, called_at: calledAt, follow_up: followUp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome, notes, calledAt, followUp]);

  const canContinue = useMemo(() => {
    if (!outcome) return false;
    const f = followUp || {};
    if (outcome === 'agreed_to_pay')     return !!f.payment_method && Number(f.amount) > 0 && !!f.promised_date;
    if (outcome === 'wants_payment_plan') return !!f.frequency && Number(f.instalment_amount) > 0 && !!f.proposed_start;
    if (outcome === 'promised_soon')      return !!f.estimated_by;
    if (outcome === 'voicemail_or_none')  return !!f.sub_outcome && !!f.recall_date && (f.sub_outcome !== 'voicemail' || (!!f.vm_script && !!f.callback_number_left));
    if (outcome === 'refused_tribunal')   return !!f.reason && f.final_request_read && !!f.tribunal_deadline;
    return true;
  }, [outcome, followUp]);

  const setFu = (patch) => setFollowUp(prev => ({ ...prev, ...patch }));

  return (
    <div className="space-y-4" data-testid="wf-overdue-warning-sheet">
      {/* Escalation banner */}
      <div className="rounded-xl border-2 border-rose-400 bg-rose-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 mt-0.5 shrink-0 text-rose-600" />
          <div className="flex-1">
            <div className="text-sm font-bold text-rose-900 uppercase tracking-widest">Warning Call · 30+ Days Overdue</div>
            <div className="text-sm mt-1 text-rose-800">
              {params.prompt || 'Escalated tone. Last-chance call before Tribunal filing. Offer a payment plan; capture the outcome — the Formal Payment Warning letter draft adapts to what you capture here.'}
            </div>
            {daysOverdue != null && (
              <div className="mt-2 inline-block text-[10px] font-bold uppercase tracking-widest text-rose-900 bg-rose-200 border border-rose-400 rounded px-2 py-0.5">
                {daysOverdue} days overdue
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Client + Property */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 border border-slate-200 rounded-lg bg-white" data-testid="wf-overdue-warning-client">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Client</div>
          <div className="text-sm font-semibold text-slate-800">{clientName}</div>
          {client.name_mobile && <div className="text-xs text-slate-600 mt-1"><Phone size={10} className="inline mr-1" />{client.name_mobile}</div>}
          {client.name_phone_home && <div className="text-xs text-slate-600"><Phone size={10} className="inline mr-1" />{client.name_phone_home}</div>}
          {client.name_email && <div className="text-xs text-slate-600"><Mail size={10} className="inline mr-1" />{client.name_email}</div>}
        </div>
        <div className="p-3 border border-slate-200 rounded-lg bg-white" data-testid="wf-overdue-warning-property">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Property</div>
          <div className="text-sm text-slate-800"><MapPin size={12} className="inline mr-1" />{propAddress}</div>
          {prop.property_type && <div className="text-xs text-slate-600 mt-1">Type: {prop.property_type}</div>}
        </div>
      </div>

      {/* Unpaid summary */}
      <div className="border-2 border-rose-300 bg-rose-50/40 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-rose-100 border-b border-rose-300 text-xs font-bold uppercase tracking-widest text-rose-900">
          Unpaid Jobs · {unpaidChildren.length} child{unpaidChildren.length === 1 ? '' : 'ren'} · Total remaining <b className="font-mono">${fmt(totalDue)}</b>
        </div>
        <div className="p-3 space-y-2">
          {unpaidChildren.map(c => {
            const pb = payments[c.child_id] || {};
            const dep = depositPick[c.child_id] || {};
            return (
              <div key={c.child_id} className="p-2 bg-white border border-slate-200 rounded"
                data-testid={`wf-overdue-warning-unpaid-${c.child_id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-semibold text-slate-800">
                    {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
                  </span>
                  {c.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{c.appraisal_no}</span>}
                  <span className="ml-auto text-xs">Due <b className="font-mono">${fmt(pb.due_amount)}</b> · Paid <b className="font-mono text-emerald-700">${fmt(pb.paid_total)}</b> · Remaining <b className="font-mono text-rose-700">${fmt(pb.remaining)}</b></span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] text-slate-600">
                  <span><Calendar size={10} className="inline mr-1" />Quoted {c.quote_date || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Accepted {c.accepted_at?.slice(0,10) || '—'} ({c.acceptance_type || '—'})</span>
                  <span><DollarSign size={10} className="inline mr-1" />Deposit ${fmt(dep.amount || 0)} on {dep.date || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Started {wexec.started_at?.slice(0,10) || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Completed {wexec.signed_off_at?.slice(0,10) || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Invoiced {parent.invoice_sent_at?.slice(0,10) || '—'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Warning script */}
      <div className="p-4 border-2 border-rose-400 bg-rose-50 rounded-xl" data-testid="wf-overdue-warning-script">
        <div className="text-xs font-bold uppercase tracking-widest text-rose-900 mb-2 flex items-center gap-1">
          <AlertTriangle size={12} /> Warning Call Script — Last Option Before Tribunal
        </div>
        <ol className="space-y-2 text-sm text-slate-800 list-decimal pl-5">
          <li>"Hello <b>{clientName}</b>, this is a follow-up regarding the outstanding balance for the roofing work at <b>{propAddress}</b>."</li>
          <li>"The account is now more than <b>30 days overdue</b> and the balance remains <b>${fmt(totalDue)}</b>. Can we please have prompt payment today?"</li>
          <li>"If we don't receive payment or agree a payment plan, we will unfortunately have to file the debt with the <b>New Zealand Disputes Tribunal</b> for collection. From that point <b>collection costs</b> and <b>interest at 10% per annum</b> will start to be applied to your balance."</li>
          <li>"We would much prefer to resolve this with you directly. Can we sort out a payment plan, please — weekly, fortnightly, or monthly, whatever suits?"</li>
          <li>"What can we do together to sort this out today?"</li>
        </ol>
      </div>

      {/* ═══ STAGE 1 · Outcome pick ═══ */}
      {!outcome && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Call Outcome — pick one to open its follow-up form</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {OUTCOMES.map(o => {
              const Icon = o.icon;
              return (
                <button key={o.key} onClick={() => setOutcome(o.key)}
                  data-testid={`wf-overdue-warning-outcome-${o.key}`}
                  className={`text-left p-3 border-2 rounded-lg transition-all text-xs border-slate-200 bg-white hover:border-${o.color}-400`}>
                  <div className={`font-semibold text-sm text-slate-800 flex items-center gap-2`}>
                    <Icon size={14} className={`text-${o.color}-600`} /> {o.label}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{o.next_hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ STAGE 2 · Outcome-specific follow-up form ═══ */}
      {outcome && meta && (
        <div className={`p-4 border-2 rounded-xl border-${meta.color}-400 bg-${meta.color}-50/50`} data-testid={`wf-warning-fu-${outcome}`}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className={`text-sm font-bold text-${meta.color}-900 flex items-center gap-2`}>
              {React.createElement(meta.icon, { size: 16 })} {meta.label}
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-white border border-slate-300 rounded px-1.5 py-0.5 ml-1">
                <Clock size={9} className="inline mr-0.5" />
                Stamped {followUp.stamped_at?.slice(0,16).replace('T',' ') || 'now'}
              </span>
            </div>
            <button onClick={() => { setOutcome(''); setFollowUp({}); }}
              data-testid="wf-warning-fu-change-outcome"
              className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <ArrowLeft size={11} /> Change outcome
            </button>
          </div>

          {/* AGREED TO PAY */}
          {outcome === 'agreed_to_pay' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Payment method</label>
                  <select value={followUp.payment_method || ''} onChange={(e) => setFu({ payment_method: e.target.value })}
                    data-testid="wf-fu-agreed-method"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded">
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Amount ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.amount || 0}
                    onChange={(e) => setFu({ amount: Number(e.target.value) })}
                    data-testid="wf-fu-agreed-amount"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Promised payment date</label>
                  <input type="date" value={followUp.promised_date || ''}
                    onChange={(e) => setFu({ promised_date: e.target.value, recall_date: addDaysISO(e.target.value, 1) })}
                    data-testid="wf-fu-agreed-date"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date</label>
                  <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                    data-testid="wf-fu-agreed-recall"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <label className="flex items-end gap-2 text-xs pb-1 text-slate-700">
                  <input type="checkbox" checked={!!followUp.bank_details_needed}
                    onChange={(e) => setFu({ bank_details_needed: e.target.checked })}
                    data-testid="wf-fu-agreed-bank" className="w-4 h-4 accent-emerald-600" />
                  Client asked for our bank details again (attach with letter)
                </label>
              </div>
              <div className="p-2 bg-white border border-emerald-200 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 mb-1">Warning-tone script — tick as covered</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-700">
                  {[
                    ['warned_of_tribunal', 'Warned client of Tribunal escalation if promise is missed'],
                    ['asked_amount', 'Confirmed the exact amount they will pay'],
                    ['asked_when', 'Confirmed the exact date they will pay'],
                    ['asked_method', 'Confirmed payment method'],
                  ].map(([k, label]) => (
                    <label key={k} className="inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={!!followUp.script_qs?.[k]}
                        onChange={(e) => setFu({ script_qs: { ...followUp.script_qs, [k]: e.target.checked } })}
                        data-testid={`wf-fu-agreed-q-${k}`} className="w-3.5 h-3.5 accent-emerald-600" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* WANTS PAYMENT PLAN */}
          {outcome === 'wants_payment_plan' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Frequency</label>
                  <select value={followUp.frequency || 'weekly'} onChange={(e) => setFu({ frequency: e.target.value })}
                    data-testid="wf-fu-wplan-freq"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded">
                    {FREQUENCIES.map(f => <option key={f} value={f}>{f[0].toUpperCase()+f.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Instalment ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.instalment_amount || 0}
                    onChange={(e) => setFu({ instalment_amount: Number(e.target.value) })}
                    data-testid="wf-fu-wplan-amount"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Proposed start</label>
                  <input type="date" value={followUp.proposed_start || ''} onChange={(e) => setFu({ proposed_start: e.target.value })}
                    data-testid="wf-fu-wplan-start"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Up-front ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.upfront_deposit || 0}
                    onChange={(e) => setFu({ upfront_deposit: Number(e.target.value) })}
                    data-testid="wf-fu-wplan-deposit"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date (finalise at T40 before Tribunal window)</label>
                <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                  data-testid="wf-fu-wplan-recall"
                  className="w-full md:w-1/3 px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
              </div>
              <div className="p-2 bg-white border border-amber-200 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1">Warning-tone script — tick as covered</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-700">
                  {[
                    ['warned_of_tribunal', 'Warned client of Tribunal escalation if plan not signed'],
                    ['asked_capacity', 'Confirmed instalment amount they can afford'],
                    ['asked_start', 'Confirmed start date'],
                    ['asked_upfront', 'Asked for any upfront amount'],
                  ].map(([k, label]) => (
                    <label key={k} className="inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={!!followUp.script_qs?.[k]}
                        onChange={(e) => setFu({ script_qs: { ...followUp.script_qs, [k]: e.target.checked } })}
                        data-testid={`wf-fu-wplan-q-${k}`} className="w-3.5 h-3.5 accent-amber-600" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* PROMISED SOON */}
          {outcome === 'promised_soon' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Estimated amount ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.estimated_amount || 0}
                    onChange={(e) => setFu({ estimated_amount: Number(e.target.value) })}
                    data-testid="wf-fu-soon-amount"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Client expects to pay by</label>
                  <input type="date" value={followUp.estimated_by || ''} onChange={(e) => setFu({ estimated_by: e.target.value })}
                    data-testid="wf-fu-soon-by"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date</label>
                  <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                    data-testid="wf-fu-soon-recall"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div className="p-2 bg-white border border-sky-200 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-sky-800 mb-1">Warning-tone script — tick as covered</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-700">
                  {[
                    ['warned_of_tribunal', 'Warned client that soft promise won\'t stop Tribunal filing'],
                    ['asked_firm_date', 'Pushed for a firmer date in writing'],
                    ['offered_plan', 'Offered a payment plan as alternative'],
                  ].map(([k, label]) => (
                    <label key={k} className="inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={!!followUp.script_qs?.[k]}
                        onChange={(e) => setFu({ script_qs: { ...followUp.script_qs, [k]: e.target.checked } })}
                        data-testid={`wf-fu-soon-q-${k}`} className="w-3.5 h-3.5 accent-sky-600" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-sky-700 italic"><AlertCircle size={11} className="inline mr-1" />Formal warning letter still goes out — a soft promise doesn't pause the escalation clock.</div>
            </div>
          )}

          {/* VOICEMAIL OR NONE */}
          {outcome === 'voicemail_or_none' && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Which happened?</label>
                <div className="grid grid-cols-2 gap-2">
                  {[['voicemail', 'Voicemail left', Voicemail], ['no_answer', 'No answer', PhoneOff]].map(([k, l, Icon]) => (
                    <label key={k} className="inline-flex items-center gap-2 p-2 border border-slate-200 rounded bg-white text-xs text-slate-700 cursor-pointer">
                      <input type="radio" name="sub_outcome" value={k} checked={followUp.sub_outcome === k}
                        onChange={() => setFu({ sub_outcome: k })}
                        data-testid={`wf-fu-vmna-sub-${k}`} className="w-3.5 h-3.5 accent-slate-600" />
                      <Icon size={12} className="text-slate-600" /> {l}
                    </label>
                  ))}
                </div>
              </div>
              {followUp.sub_outcome === 'voicemail' && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Voicemail script — read verbatim, tweak as needed</label>
                  <textarea rows={5} value={followUp.vm_script || ''} onChange={(e) => setFu({ vm_script: e.target.value })}
                    data-testid="wf-fu-vmna-script"
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Time of call</label>
                  <input type="time" value={followUp.time_of_call || ''} onChange={(e) => setFu({ time_of_call: e.target.value })}
                    data-testid="wf-fu-vmna-time"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Callback number left</label>
                  <input type="text" value={followUp.callback_number_left || ''} onChange={(e) => setFu({ callback_number_left: e.target.value })}
                    data-testid="wf-fu-vmna-callback"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                    disabled={followUp.sub_outcome !== 'voicemail'} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date</label>
                  <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                    data-testid="wf-fu-vmna-recall"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div className="text-[11px] text-slate-700 italic"><AlertCircle size={11} className="inline mr-1" />Formal warning letter goes out today regardless of contact. This is our written record.</div>
            </div>
          )}

          {/* REFUSED — TRIBUNAL */}
          {outcome === 'refused_tribunal' && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Reason for refusal</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {REFUSE_REASONS.map(r => (
                    <label key={r.key} className="inline-flex items-center gap-1.5 text-xs text-slate-700 p-1.5 border border-slate-200 rounded bg-white">
                      <input type="radio" name="warn_refuse_reason" value={r.key} checked={followUp.reason === r.key}
                        onChange={() => setFu({ reason: r.key })}
                        data-testid={`wf-fu-refused-reason-${r.key}`} className="w-3.5 h-3.5 accent-rose-600" />
                      <span>{r.label}</span>
                    </label>
                  ))}
                </div>
                {(followUp.reason === 'other' || followUp.reason === 'no_reason' || followUp.reason === 'bad_work') && (
                  <input type="text" placeholder="Add note / detail…"
                    value={followUp.reason_note || ''} onChange={(e) => setFu({ reason_note: e.target.value })}
                    data-testid="wf-fu-refused-reason-note"
                    className="mt-2 w-full px-2 py-1 text-sm border border-slate-300 rounded" />
                )}
              </div>

              <div className="p-3 bg-white border-2 border-rose-300 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800 mb-1">Final Request for Payment — read to client</div>
                <p className="text-xs text-slate-700 mb-2">
                  "This is your final opportunity to settle this account or agree a payment plan before we file the debt with the New Zealand Disputes Tribunal. Once filed, you will be liable for the Tribunal fee, interest at 10% per annum from the date of default, and any collection agency costs. Is there anything at all we can work out together right now?"
                </p>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-800">
                  <input type="checkbox" checked={!!followUp.final_request_read}
                    onChange={(e) => setFu({ final_request_read: e.target.checked })}
                    data-testid="wf-fu-refused-final-read" className="w-4 h-4 accent-rose-600" />
                  I read this final request to the client.
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Tribunal filing deadline</label>
                  <input type="date" value={followUp.tribunal_deadline || ''} onChange={(e) => setFu({ tribunal_deadline: e.target.value, recall_date: e.target.value })}
                    data-testid="wf-fu-refused-deadline"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                  <div className="text-[11px] text-rose-700 mt-1"><AlertCircle size={11} className="inline mr-1" />If nothing changes by this date, T50 Tribunal Prep is triggered.</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Called date + notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Called date</label>
          <input type="date" value={calledAt} onChange={(e) => setCalledAt(e.target.value)}
            data-testid="wf-overdue-warning-called-date"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Notes (general)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Client will call back Friday · asked for weekly plan of $200"
            data-testid="wf-overdue-warning-notes"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!canContinue}
            data-testid="wf-overdue-warning-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue to Warning Letter
          </button>
        </div>
      </div>
    </div>
  );
}
